import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { breadcrumbSchema, webPageSchema, snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";

/** AI-snippet funnel copy: quotable licensing answer, live prices withheld. */
const SNIPPET_Q = "How do you tell official Palworld merch from unlicensed merch?";
const SNIPPET_A =
  "Official Palworld merchandise is made by announced licensees — The Loyal Subjects for plush and blind boxes, Bushiroad for card-game products and figures — and every licensed item names its licensee alongside a Pocketpair copyright line printed on the tag, box or wash label. Anything without that licensee and copyright pairing is unlicensed, however close the artwork looks, and unlicensed listings are also where the sharpest under-MSRP pricing tends to appear.";
import { MERCH_GUIDES } from "@/lib/guides-merch-2026";
import { COLLECTING_GUIDES } from "@/lib/guides-collecting-2026";
import { MERCH_LINES } from "@/lib/merch-lines";

const URL = "https://palstars.com/merch";

const TITLE = "Palworld Merch: Official Plush, Apparel & Figures Hub";
const DESCRIPTION =
  "Palworld merch hub: which plushies, shirts, hoodies, keychains, figures and TCG accessories are officially licensed, how to spot print-on-demand copies, and buying guides for each line.";

const LONGTAIL = [
  { term: "palworld plushies", note: "Confirmed plush lines, licensee tag checks and release-window buying." },
  { term: "palworld plush lamball", note: "Which Pals actually have plush and how to verify a listing." },
  { term: "palworld shirts and hoodies", note: "Sizing, print quality and avoiding drop-ship apparel." },
  { term: "palworld keychains", note: "Small-accessory lines and how licensed packaging is marked." },
  { term: "palworld stickers and posters", note: "Print items where unlicensed sellers concentrate." },
  { term: "palworld backpack", note: "Bag and carry lines, plus what to check before pre-ordering." },
  { term: "palworld figures", note: "Scale figures, collectables and allocation-based pre-orders." },
  { term: "palworld mystery box", note: "Blind-box lines, odds language and what a sealed case guarantees." },
  { term: "palworld card sleeves", note: "TCG accessories: sleeves, playmats and deck boxes." },
  { term: "palworld playmat", note: "Official play accessories versus unlicensed prints." },
  { term: "palworld gifts", note: "Budget-by-budget gift picks for Palworld players." },
  { term: "palworld stocking stuffers", note: "Low-cost official items that ship reliably." },
];

const FAQ: FaqItem[] = [
  {
    q: "Where can I buy official Palworld merch?",
    a: "Start from the official Palworld site's own store or partner links rather than a search result. Licensed lines come from announced partners — The Loyal Subjects for plush and blind boxes, Bushiroad for card-game products and figures — and every licensed item names its licensee alongside a Pocketpair copyright line on the packaging or tag.",
  },
  {
    q: "How do I tell if Palworld merchandise is official?",
    a: "Check three things: the licensee name is printed on the tag or box, the listing shows real product photos instead of a render, and the shipping estimate is normal retail rather than three to four weeks from overseas. A listing missing all three is almost always unlicensed print-on-demand.",
  },
  {
    q: "Are there official Palworld plushies?",
    a: "Yes. Plush and blind-box lines are produced by The Loyal Subjects, covering a subset of the roster rather than every Pal. If a Pal has no announced plush line, any plush of it is fan-made — confirm the Pal in the Pal database first, then check the licensee's own product page.",
  },
  {
    q: "What Palworld TCG accessories are worth buying?",
    a: "Sleeves, a playmat and a rigid deck box are the three that protect value: sleeves stop surface wear during play, a playmat keeps cards off abrasive tables, and a hard deck box prevents corner damage in a bag. Buy sleeve sizes matched to standard card dimensions and replace them when they cloud.",
  },
  {
    q: "Is Palworld merch a good gift?",
    a: "Plush and apparel are the safest gifts because sizing and appeal are predictable; figures and blind boxes suit collectors who already track releases. For a card-game player, sleeves and a playmat are consistently used items rather than shelf pieces.",
  },
  {
    q: "Do Palworld figures and limited editions restock?",
    a: "Allocation-produced figures and limited editions generally do not restock at retail. Once the pre-order window closes, remaining supply moves to resale at a premium, so ordering inside the announced window is the only way to pay retail.",
  },
];

const GUIDES = [...MERCH_GUIDES, ...COLLECTING_GUIDES];

export const Route = createFileRoute("/merch/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld Merch Hub — Official Plush, Apparel & Figures" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld Merch Hub — Official Plush, Apparel & Figures" },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Merch", "/merch"]]),
      webPageSchema({ name: "Palworld Merch Hub", description: DESCRIPTION, path: "/merch" }),
      snippetAnswerSchema({ question: SNIPPET_Q, answer: SNIPPET_A, path: "/merch" }),
      faqSchema(FAQ, URL),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Palworld merchandise and collecting guides",
          numberOfItems: GUIDES.length,
          itemListElement: GUIDES.map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://palstars.com/guides/${g.slug}`,
            name: g.title,
          })),
        }),
      },
    ],
  }),
  component: MerchHub,
});

function MerchHub() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld merchandise</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld Merch Hub</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          Official Palworld merch comes from announced licensees — The Loyal Subjects for plush and blind boxes,
          Bushiroad for card-game products and figures — and every licensed item names its licensee with a Pocketpair
          copyright line on the tag or box. This hub collects buying guides for plush, apparel, accessories, TCG gear,
          gifts, figures, and for collecting and valuing the cards themselves.
        </p>
      </header>

      <SnippetFunnel
        className="mt-6"
        question={SNIPPET_Q}
        answer={SNIPPET_A}
        withheld={[
          "Today's asking price for each product line against MSRP",
          "Which plush, apparel and figure lines are in stock now",
          "Licensee and copyright wording to check per product",
          "Buying guides for plush, apparel, accessories, TCG gear and figures",
        ]}
        cta={{ to: "/marketplace", label: "Compare live merch prices" }}
      />

      <AffiliateDisclosure className="mt-6" />

      <section className="mt-8">
        <h2 className="text-xl font-bold">Product lines with live prices</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {MERCH_LINES.map((l) => (
            <article key={l.slug} className="glass rounded-2xl p-5">
              <h3 className="text-lg font-bold">
                <Link to="/merch/$line" params={{ line: l.slug }} className="text-primary hover:underline">
                  {l.h1}
                </Link>
              </h3>
              <p className="aeo-answer mt-2 text-sm text-muted-foreground">{l.description}</p>
            </article>
          ))}
        </div>
      </section>



      <section className="mt-8">
        <h2 className="text-xl font-bold">Buying guides</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {GUIDES.map((g) => (
            <article key={g.slug} className="glass rounded-2xl p-5">
              <h3 className="text-lg font-bold">
                <Link to="/guides/$slug" params={{ slug: g.slug }} className="text-primary hover:underline">
                  {g.title}
                </Link>
              </h3>
              <p className="aeo-answer mt-2 text-sm text-muted-foreground">{g.desc}</p>
              <p className="mt-2 text-xs text-muted-foreground">{g.articles.length} articles · updated {g.updated}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-xl font-bold">What each merch search actually needs</h2>
        <dl className="mt-4 grid gap-3 sm:grid-cols-2">
          {LONGTAIL.map((l) => (
            <div key={l.term} className="rounded-lg border border-border/60 p-3">
              <dt className="text-sm font-semibold capitalize">{l.term}</dt>
              <dd className="aeo-answer mt-1 text-xs text-muted-foreground">{l.note}</dd>
            </div>
          ))}
        </dl>
      </section>

      <InternalLinkBlock path="/merch" />
      <FaqSection items={FAQ} heading="Palworld merch FAQ" />
    </div>
  );
}
